import logo from './img/iQPro+ Logo.webp';
import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import DrawSVGPlugin from 'gsap/DrawSVGPlugin';
import { Link } from 'react-router-dom';

const API_DOCS_PROD_URL = 'https://api.basyspro.com/iqsaas/v1/scalar/';
const API_DOCS_SANDBOX_URL = 'https://sandbox.api.basyspro.com/iqsaas/v1/scalar/';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(DrawSVGPlugin);

export default function Header() {
  const container = useRef();

  useGSAP(
    () => {
      let featureMenu = gsap.fromTo(
        '.dropdown-features',
        { opacity: 0, scale: 0 },
        { opacity: 1, scale: 1, duration: 0.2, paused: true, transformOrigin: 'center top' }
      );

      let integrationsMenu = gsap.fromTo(
        '.dropdown-integrations',
        { autoAlpha: 0, scale: 0, pointerEvents: 'none' },
        {
          autoAlpha: 1,
          scale: 1,
          duration: 0.2,
          paused: true,
          pointerEvents: 'auto',
          transformOrigin: 'left top',
          ease: 'power2.out'
        }
      );

      let resourcesMenu = gsap.fromTo(
        '.dropdown-resources',
        { autoAlpha: 0, scale: 0, pointerEvents: 'none' },
        {
          autoAlpha: 1,
          scale: 1,
          duration: 0.2,
          paused: true,
          pointerEvents: 'auto',
          transformOrigin: 'left top',
          ease: 'power2.out'
        }
      );

      let loginMenu = gsap.fromTo(
        '.dropdown-login',
        { opacity: 0, scale: 0 },
        { opacity: 1, scale: 1, duration: 0.2, paused: true, transformOrigin: 'left top' }
      );
      resourcesMenu.eventCallback('onReverseComplete', () =>
        gsap.set('.dropdown-resources', { pointerEvents: 'none' })
      );

      // 3rd level dropdown (Developer > API Documentation)
      let docsMenu = gsap.fromTo(
        '.dropdown-docs',
        { autoAlpha: 0, scale: 0, pointerEvents: 'none' },
        {
          autoAlpha: 1,
          scale: 1,
          duration: 0.2,
          paused: true,
          pointerEvents: 'auto',
          transformOrigin: 'left top',
          ease: 'power2.out'
        }
      );
      docsMenu.eventCallback('onReverseComplete', () =>
        gsap.set('.dropdown-docs', { pointerEvents: 'none' })
      );

      let featuresButton = document.getElementById('featuresButton');
      let dropdown = document.getElementById('features');
      let dropdown2 = document.getElementById('resources');
      let integrationsButton = document.getElementById('integrationsButton');
      let dropdown4 = document.getElementById('integrationsD');

      let loginButton = document.getElementById('loginButton');
      let dropdown3 = document.getElementById('login');

      let apiDocsDropdown = document.getElementById('apiDocs');

      featuresButton.addEventListener('mouseenter', () => featureMenu.play());
      dropdown.addEventListener('mouseleave', () => featureMenu.reverse());
      integrationsButton.addEventListener('mouseenter', () => integrationsMenu.play());
      dropdown4.addEventListener('mouseleave', () => integrationsMenu.reverse());

      loginButton.addEventListener('mouseenter', () => loginMenu.play());
      dropdown3.addEventListener('mouseleave', () => loginMenu.reverse());

      // ---- Developer dropdown + API Docs flyout (smooth close, no bounce) ----
      const resourcesPanel = document.querySelector('.dropdown-resources');
      const docsPanel = document.querySelector('.dropdown-docs');

      let devLocked = false;
      let devUnlockCall;

      // single GSAP-style delayed close (no stacked timeouts)
      const devCloseCall = gsap
        .delayedCall(0.22, () => {
          const stillHovering =
            dropdown2?.matches(':hover') ||
            resourcesPanel?.matches(':hover') ||
            apiDocsDropdown?.matches(':hover') ||
            docsPanel?.matches(':hover');

          if (stillHovering) return;

          // lockout prevents: fade out -> re-enter edge -> pop back -> fade out again
          devLocked = true;
          devUnlockCall?.kill();
          devUnlockCall = gsap.delayedCall(0.25, () => {
            devLocked = false;
          });

          docsMenu.reverse();
          resourcesMenu.reverse();
        })
        .pause();

      const devOpen = () => {
        if (devLocked) return;
        devCloseCall.pause(0);
        resourcesMenu.play();
      };

      const devScheduleClose = () => {
        devCloseCall.restart(true);
      };

      // Keep Developer open while hovering button/menu/flyout
      dropdown2.addEventListener('mouseenter', devOpen);
      dropdown2.addEventListener('mouseleave', devScheduleClose);

      resourcesPanel?.addEventListener('mouseenter', devOpen);
      resourcesPanel?.addEventListener('mouseleave', devScheduleClose);

      // API Docs flyout: open while hovering row or flyout; close when leaving both
      const docsOpen = () => {
        if (devLocked) return;
        devOpen();
        docsMenu.play();
      };

      const docsClose = (e) => {
        const to = e?.relatedTarget;
        if (to && (apiDocsDropdown?.contains(to) || docsPanel?.contains(to))) return;
        docsMenu.reverse();
      };

      apiDocsDropdown.addEventListener('mouseenter', docsOpen);
      apiDocsDropdown.addEventListener('mouseleave', docsClose);

      docsPanel?.addEventListener('mouseenter', docsOpen);
      docsPanel?.addEventListener('mouseleave', (e) => {
        docsClose(e);
        devScheduleClose();
      });
    },
    { scope: container }
  );

  return (
    <section className="max-[1000px]:hidden">
      <div className="row nav-row">
        <header
          ref={container}
          className="flex self-stretch justify-between items-center px-2 py-2 rounded-[999px] bg-white box-shadow"
        >
          <a className="logo" href="/">
            <img src={logo} alt="iQ Pro+ Logo" />
          </a>

          <nav className={`flex items-center justify-center h-auto gap-0`}>
            <div className="dropdown" id="features">
              <button className="button button-secondary has-arrow" id="featuresButton">
                Features
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-2 dropdown-content dropdown-features">
                <Link to="/features/terminal" className="subnav-item">
                  <p className="text-black sub-nav-header has-icon terminal !mb-3">Terminal</p>
                  <p className="text-black">
                    Securely take payments on any device with an internet connection.
                  </p>
                </Link>

                <Link to="/features/customers" className="subnav-item">
                  <p className="text-black sub-nav-header has-icon customers !mb-3">Customers</p>
                  <p className="text-black">
                    Securely store and manage your customers payment details.
                  </p>
                </Link>

                <Link to="/features/simplePay" className="subnav-item">
                  <p className="text-black sub-nav-header has-icon simple-pay !mb-3">Simple Pay</p>
                  <p className="text-black">
                    Customize and send your customers a pre-built payment form.
                  </p>
                </Link>

                <Link to="/features/invoicing" className="subnav-item">
                  <p className="text-black sub-nav-header has-icon invoices !mb-3">Invoicing</p>
                  <p className="text-black">
                    Create, send, and track payments effortlessly, all from one convenient platform.
                  </p>
                </Link>

                <Link to="/features/recurringBilling" className="subnav-item">
                  <p className="text-black sub-nav-header has-icon recurring-billing !mb-3">
                    Recurring Billing
                  </p>
                  <p className="text-black">
                    Recurring payments that simplify the billing process.
                  </p>
                </Link>

                <Link to="/mobile" className="subnav-item">
                  <p className="text-black sub-nav-header has-icon mobile-app !mb-3">Mobile App</p>
                  <p className="text-black">Process payments from your phone or tablet.</p>
                </Link>

                <Link to="/features/reporting" className="subnav-item">
                  <p className="text-black sub-nav-header has-icon reporting !mb-3">Reporting</p>
                  <p className="text-black">
                    View, reconcile, and analyze transaction and settlement data.
                  </p>
                </Link>

                <Link to="/features/physicalTerminals" className="subnav-item">
                  <p className="text-black sub-nav-header has-icon physical-terminals !mb-3">
                    Physical Terminals
                  </p>
                  <p className="text-black">
                    Process card transactions wherever your business takes you.
                  </p>
                </Link>
              </div>
            </div>

            <div className="dropdown" id="integrationsD">
              <button className="button button-secondary has-arrow" id="integrationsButton">
                Integrations
              </button>
              <div className="grid grid-cols-1 dropdown-content dropdown-integrations">
                <Link to="/integrations" className="subnav-item">
                  <p className="text-black sub-nav-header has-icon integrations !mb-3">
                    Integrations
                  </p>
                  <p className="text-black">
                    Learn more about how iQ Pro+ can integrate with your software.
                  </p>
                </Link>

                <div className="w-[220px] h-[1px] bg-[#E8E8ED] self-center justify-self-center"></div>

                <Link to="/integrations-epic" className="subnav-item">
                  <p className="text-black sub-nav-header ">Epic Health</p>
                </Link>
                <a
                  href="https://www.basyspro.com/quickbooks"
                  target="_blank"
                  rel="noreferrer"
                  className="subnav-item"
                >
                  <p className="text-black sub-nav-header ">QuickBooks</p>
                </a>
                <Link to="/integrations-e-commerce" className="subnav-item">
                  <p className="text-black sub-nav-header ">E-Commerce</p>
                </Link>
              </div>
            </div>

            <div className="dropdown" id="resources">
              <button className="button button-secondary has-arrow" id="resourcesButton">
                Developer
              </button>

              <div className="grid grid-cols-1 dropdown-content dropdown-resources">
                {/* 3rd-level dropdown */}
                <div className="dropdown dropdown-submenu" id="apiDocs">
                  <button
                    type="button"
                    className="subnav-item subnav-item-submenu"
                    id="apiDocsButton"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-black sub-nav-header has-icon documentation">
                        API Documentation
                      </p>
                      <span className="submenu-caret" aria-hidden="true">
                        ›
                      </span>
                    </div>
                  </button>

                  <div className="grid grid-cols-1 dropdown-content dropdown-docs">
                    <a
                      className="subnav-item"
                      href={API_DOCS_PROD_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <p className="text-black sub-nav-header">Production Docs</p>
                    </a>

                    <a
                      className="subnav-item"
                      href={API_DOCS_SANDBOX_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <p className="text-black sub-nav-header">Sandbox Docs</p>
                    </a>
                  </div>
                </div>

                <Link className="subnav-item" to="/developer-tokenization">
                  <p className="text-black sub-nav-header has-icon tokenization">Tokenization</p>
                </Link>

                <Link className="subnav-item" to="/developer-test-data">
                  <p className="text-black sub-nav-header has-icon test-data">Test Data</p>
                </Link>

                <Link className="subnav-item" to="/developer-physical-terminals">
                  <p className="text-black sub-nav-header has-icon developer-physical">
                    Physical Terminals
                  </p>
                </Link>

                <Link className="subnav-item" to="/developer-sandbox-emulator">
                  <p className="text-black sub-nav-header has-icon developer-physical">
                    Sandbox Emulator
                  </p>
                </Link>

                <Link className="subnav-item" to="/developer-glossary">
                  <p className="text-black sub-nav-header has-icon glossary">Glossary</p>
                </Link>

                <Link className="subnav-item" to="/release-notes">
                  <p className="text-black sub-nav-header has-icon status">Release Notes</p>
                </Link>

                <Link className="subnav-item" to="/developer-webhooks">
                  <p className="text-black sub-nav-header has-icon webhooks">Webhooks</p>
                </Link>
                <Link className="subnav-item" to="/developer-customer-vault">
                  <p className="text-black sub-nav-header">Customer Vault</p>
                </Link>
                <Link className="subnav-item" to="/developer-subscriptions">
                  <p className="text-black sub-nav-header">Subscriptions</p>
                </Link>
              </div>
            </div>

            <Link to="/support" className="button button-secondary has-arrow">
              Support
            </Link>

            <div className="dropdown" id="login">
              <button className="button button-secondary has-arrow" id="loginButton">
                Login
              </button>

              <div className="grid grid-cols-1 dropdown-content dropdown-login">
                <a
                  className="subnav-item"
                  href="https://basyspro.net/Default.aspx"
                  target="_blank"
                  rel="noreferrer"
                >
                  <p className="text-black sub-nav-header">Basys IQ</p>
                </a>

                <a
                  className="subnav-item"
                  href="https://app.basysiqpro.com/auth"
                  target="_blank"
                  rel="noreferrer"
                >
                  <p className="text-black sub-nav-header">iQ Pro</p>
                </a>

                <a
                  className="subnav-item"
                  href="https://app.iqpro.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  <p className="text-black sub-nav-header">iQ Pro+</p>
                </a>
              </div>
            </div>
          </nav>

          <Link
            to="/bookDemo"
            className="button button-primary has-arrow has-arrow-white"
            id="navTopButtonNormal"
          >
            Contact Sales
          </Link>
        </header>
      </div>
    </section>
  );
}
